import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "wouter";
import { getDraft, sampleDraft, type AccountDraft } from "@/lib/draftStore";
import { Printer, ArrowLeft, AlertTriangle } from "lucide-react";
import { getSession, supabase } from "@/lib/session";

// Matches the constant in NewAccount.tsx — a sessionStorage breadcrumb
// that signals the user clicked "Back to wizard" so the wizard mount
// restores the submitted draft instead of starting fresh.
const WIZARD_RETURN_FLAG = "aip:wizard-return:add-prospect";
function flagReturnToWizard() {
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.setItem(WIZARD_RETURN_FLAG, "1");
    }
  } catch {
    /* ignore — falling back to empty wizard is acceptable */
  }
}

function bandFor(score: number): "red" | "amber" | "green" {
  if (score >= 70) return "green";
  if (score >= 40) return "amber";
  return "red";
}
function bandLabel(b: "red" | "amber" | "green") {
  return b === "green" ? "Green" : b === "amber" ? "Orange" : "Red";
}
function bandSwatch(b: "red" | "amber" | "green") {
  if (b === "green") return "text-emerald-300 bg-emerald-500/10 ring-emerald-500/30";
  if (b === "amber") return "text-orange-300 bg-orange-500/10 ring-orange-500/30";
  return "text-rose-300 bg-rose-500/10 ring-rose-500/30";
}
function barColor(b: "red" | "amber" | "green") {
  if (b === "green") return "bg-emerald-400";
  if (b === "amber") return "bg-orange-400";
  return "bg-rose-400";
}

function fmtMoney(v: number | "" | undefined) {
  if (typeof v !== "number" || !v) return "—";
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  return `$${(v / 1000).toFixed(0)}k`;
}

function daysBetween(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function quarterTag(d = new Date()) {
  const q = Math.floor(d.getMonth() / 3) + 1;
  return `Q${q} ${d.getFullYear()}`;
}

function aOrAn(word: string) {
  return /^[aeiou]/i.test(word) ? "an" : "a";
}

function sizeBucket(emp: number | "" | undefined) {
  if (typeof emp !== "number" || !emp) return "mid-market";
  if (emp >= 5000) return "large enterprise";
  if (emp >= 1000) return "enterprise";
  if (emp >= 250) return "mid-market";
  return "growth-stage";
}

/**
 * Shown when an AE tries to view a plan-report that belongs to another rep.
 */
function ForbiddenPanel() {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FAFAF7",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "Inter, system-ui, sans-serif",
        padding: "24px",
      }}
    >
      <div
        style={{
          maxWidth: 420,
          background: "#FFFFFF",
          border: "1px solid #E5E5E7",
          borderRadius: 12,
          padding: "32px 28px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "#8E8E94",
            marginBottom: 8,
          }}
        >
          Access denied
        </div>
        <p style={{ fontSize: 15, fontWeight: 600, color: "#0B0B0F", margin: "0 0 8px" }}>
          You do not have permission to view this page.
        </p>
        <p style={{ fontSize: 13, color: "#6B6B70", margin: "0 0 24px", lineHeight: 1.5 }}>
          Contact your manager if you believe this is an error.
        </p>
        <a
          href="#/my-plan"
          style={{
            display: "inline-block",
            padding: "10px 20px",
            background: "#0B0B0F",
            color: "#FFFFFF",
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 600,
            textDecoration: "none",
          }}
        >
          Back to dashboard
        </a>
      </div>
    </div>
  );
}

export default function PlanReport() {
  const { id } = useParams<{ id: string }>();
  const session = getSession();

  // Ownership gate: AE role must own the account referenced by :id.
  // "sample" and "new" bypass the DB check (they use in-memory data only).
  const [ownershipState, setOwnershipState] = useState<"checking" | "allowed" | "forbidden">("allowed");

  useEffect(() => {
    if (!session || !id || id === "sample" || id === "new") {
      setOwnershipState("allowed");
      return;
    }
    if (session.role !== "ae") {
      // manager / executive may view any plan-report
      setOwnershipState("allowed");
      return;
    }
    let cancelled = false;
    setOwnershipState("checking");
    (async () => {
      try {
        const sb = supabase();
        const { data: acct, error } = await sb
          .from("accounts")
          .select("owner_user_id")
          .eq("id", id)
          .single();
        if (cancelled) return;
        if (error || !acct) {
          setOwnershipState("forbidden");
          return;
        }
        setOwnershipState(
          String(acct.owner_user_id) === String(session.id) ? "allowed" : "forbidden"
        );
      } catch {
        if (!cancelled) setOwnershipState("forbidden");
      }
    })();
    return () => { cancelled = true; };
  }, [id, session?.id, session?.role]);

  const data: AccountDraft | null = useMemo(() => {
    if (id === "sample") return sampleDraft();
    if (id === "new") return getDraft() ?? sampleDraft();
    return getDraft() ?? sampleDraft();
  }, [id]);

  if (ownershipState === "checking") {
    return <div style={{ minHeight: "100vh", background: "#FAFAF7" }} aria-hidden />;
  }
  if (ownershipState === "forbidden") {
    return <ForbiddenPanel />;
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="card-shell p-8 text-center">
          <div className="text-sm text-muted-foreground mb-3">
            No plan data available.
          </div>
          <Link href="/add-prospect" className="text-sm underline">
            Start a new account
          </Link>
        </div>
      </div>
    );
  }

  const composite = Math.round(
    data.mpact.reduce((s, r) => s + r.score, 0) / data.mpact.length,
  );
  const compositeBand = bandFor(composite);
  const days = daysBetween(data.ceDecisionDate);
  const stakeholdersFilled = data.stakeholders.filter((s) => s.name);
  const today = new Date();
  // Demo anchor: spec asks for Q3 2026 on the sample so reviewers see a stable header.
  const quarter = id === "sample" ? "Q3 2026" : quarterTag(today);
  const dateStr = today.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  // Executive summary narrative
  const ceSentence =
    data.ceStatus === "no"
      ? "No firm or formal timeline identified yet — the deal depends on the buyer setting an official date."
      : `${data.ceDescription || "Timeline captured but description pending."} Confidence ${data.ceConfidence}%.`;

  const summary = `${data.company || "This account"} is a ${
    typeof data.employees === "number" ? data.employees.toLocaleString() : "—"
  }-employee ${data.industry || "—"} account in the ${data.region || "—"} region with estimated ARR potential of ${fmtMoney(
    data.arrPotential,
  )}. The current Composite score is ${composite}/100 (${bandLabel(
    compositeBand,
  )} band), reflecting ${aOrAn(sizeBucket(data.employees))} ${sizeBucket(data.employees)} opportunity profile. ${ceSentence}`;

  return (
    <div className="min-h-screen bg-background text-foreground print:bg-white print:text-black">
      <PrintStyles />

      {/* Top bar — hidden on print */}
      <header className="border-b border-border print:hidden sticky top-0 z-20 bg-background/95 backdrop-blur">
        <div className="max-w-[1100px] mx-auto px-8 py-3 flex items-center justify-between">
          <Link
            href="/add-prospect"
            onClick={flagReturnToWizard}
            className="flex items-center gap-2 text-[13px] text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to wizard
          </Link>
          <div className="flex items-center gap-2">
            {id === "sample" && (
              <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground border border-border rounded px-2 py-1">
                Sample report
              </span>
            )}
            <button
              onClick={() => window.print()}
              className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[36px] rounded border border-border text-xs hover:bg-[hsl(var(--surface-2))] transition-colors"
            >
              <Printer className="w-3.5 h-3.5" /> Print / Save as PDF
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1100px] mx-auto px-8 py-10 print:px-0 print:py-0 print:max-w-none">
        {/* Watermark */}
        <div className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-6 print:text-neutral-500">
          For internal use only · Do not distribute
        </div>

        {/* Cover header */}
        <section className="mb-12 print:mb-8 grid grid-cols-[1fr_auto] gap-6 items-start border-b border-border pb-10">
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-muted-foreground mb-3">
              {quarter} territory sales plan
            </div>
            <h1 className="text-5xl print:text-4xl font-semibold tracking-tight leading-[1.02]">
              {data.company || "Untitled account"}
            </h1>
            <p className="text-base text-muted-foreground mt-3 print:text-neutral-600">
              Prepared by {data.rep || "—"} · {dateStr}
            </p>
          </div>
          <div className="inline-flex items-center bg-[#F5F1EA] rounded-md px-3 py-2 print:bg-white print:border print:border-neutral-300">
            <img
              src="brand/mi6-attilius-horizontal.png"
              alt="Mi6 Group · powered by Attilius"
              className="h-10 w-auto block"
            />
          </div>
        </section>

        {/* Executive summary */}
        <ReportSection number="01" title="Executive summary">
          <p className="text-[17px] leading-relaxed mb-6 print:text-[14px] print:leading-snug">
            {summary}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print:grid-cols-4">
            <Kpi label="ARR potential" value={fmtMoney(data.arrPotential)} />
            <Kpi
              label="Composite score"
              value={String(composite)}
              accent={compositeBand}
            />
            <Kpi
              label="Stakeholders mapped"
              value={String(stakeholdersFilled.length)}
            />
            <Kpi
              label="Days to decision"
              value={days === null ? "—" : `${days}`}
            />
          </div>
        </ReportSection>

        {/* Composite scorecard */}
        <ReportSection number="02" title="Composite scorecard">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 print:grid-cols-2">
            {data.mpact.map((r) => {
              const b = bandFor(r.score);
              return (
                <div
                  key={r.key}
                  className="card-shell-2 p-4 print:border print:border-neutral-300 print:bg-white"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <span
                        className={`inline-flex items-center justify-center w-9 h-9 rounded ring-1 ring-inset font-semibold tabular ${bandSwatch(
                          b,
                        )}`}
                      >
                        {r.letter}
                      </span>
                      <span className="text-[14px] font-medium tracking-tight">
                        {r.label}
                      </span>
                    </div>
                    <span className="tabular text-2xl font-semibold">
                      {r.score}
                    </span>
                  </div>
                  <div className="bar-track w-full mt-1 mb-3">
                    <div
                      className={`bar-fill ${barColor(b)}`}
                      style={{ width: `${r.score}%` }}
                    />
                  </div>
                  <div className="text-[12px] text-muted-foreground italic min-h-[1.5em] print:text-neutral-600">
                    {r.evidence || "Evidence not yet captured."}
                  </div>
                </div>
              );
            })}
          </div>
        </ReportSection>

        {/* Firm / formal timeline */}
        <ReportSection number="03" title="Firm / formal timeline">
          <div className="card-shell-2 p-6 print:border print:border-neutral-300 print:bg-white">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="flex-1 min-w-[280px]">
                <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                  Status: {data.ceStatus}
                </div>
                <p className="text-lg leading-relaxed print:text-[14px]">
                  {data.ceDescription || "No description captured."}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-4 min-w-[280px]">
                <Kpi label="Decision date" value={data.ceDecisionDate || "—"} small />
                <Kpi label="Days to decision" value={days === null ? "—" : `${days}`} small />
                <Kpi label="Confidence" value={`${data.ceConfidence}%`} small />
              </div>
            </div>

            {(data.ceStatus === "no" || data.ceConfidence < 60) && (
              <div className="mt-5 flex items-start gap-3 rounded border border-orange-500/30 bg-orange-500/10 px-4 py-3 text-sm text-orange-200 print:bg-orange-50 print:text-orange-900 print:border-orange-300">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  {data.ceStatus === "no"
                    ? "No firm or formal timeline identified. The forecast for this account should be considered speculative until the buyer sets an official date."
                    : "Confidence is below 60%. Schedule a session with the economic buyer to validate the timeline and confirm the decision window."}
                </div>
              </div>
            )}
          </div>
        </ReportSection>

        {/* Stakeholder map */}
        <ReportSection number="04" title="Stakeholder map">
          <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 print:grid-cols-[1.4fr_1fr]">
            <div className="card-shell-2 overflow-hidden print:border print:border-neutral-300 print:bg-white">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
                    <th className="py-2 px-3 font-medium">Name</th>
                    <th className="py-2 px-3 font-medium">Title</th>
                    <th className="py-2 px-3 font-medium">Role</th>
                    <th className="py-2 px-3 font-medium text-right">Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {stakeholdersFilled.length === 0 && (
                    <tr>
                      <td colSpan={4} className="py-4 px-3 text-muted-foreground italic">
                        No stakeholders mapped yet.
                      </td>
                    </tr>
                  )}
                  {stakeholdersFilled.map((s, i) => (
                    <tr
                      key={i}
                      className="border-b border-border last:border-b-0"
                    >
                      <td className="py-2.5 px-3 font-medium">{s.name}</td>
                      <td className="py-2.5 px-3 text-muted-foreground">{s.title}</td>
                      <td className="py-2.5 px-3">
                        <RoleChip role={s.role || ""} />
                      </td>
                      <td className="py-2.5 px-3 text-right tabular">
                        {s.engagement}/5
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <StakeholderMatrix stakeholders={stakeholdersFilled} />
          </div>
        </ReportSection>

        {/* Sales approach & risks */}
        <ReportSection number="05" title="Sales approach &amp; risks">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 print:grid-cols-2">
            <div className="card-shell-2 p-5 print:border print:border-neutral-300 print:bg-white">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-2">
                Approach
              </div>
              <p className="text-[15px] leading-relaxed print:text-[13px]">
                {data.approach || "Approach narrative not yet captured."}
              </p>
            </div>
            <div className="card-shell-2 p-5 print:border print:border-neutral-300 print:bg-white">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
                Top risks
              </div>
              <ol className="space-y-2.5">
                {data.risks.filter(Boolean).map((r, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[14px] print:text-[13px]"
                  >
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-rose-400 shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
                {data.risks.filter(Boolean).length === 0 && (
                  <li className="text-sm text-muted-foreground italic">
                    No risks captured.
                  </li>
                )}
              </ol>
            </div>
          </div>
        </ReportSection>

        {/* 30/60/90 */}
        <ReportSection number="06" title="30 / 60 / 90 day action plan">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:grid-cols-3">
            {[
              { label: "Next 30 days", rows: data.next30 },
              { label: "Next 60 days", rows: data.next60 },
              { label: "Next 90 days", rows: data.next90 },
            ].map((col) => (
              <div
                key={col.label}
                className="card-shell-2 overflow-hidden print:border print:border-neutral-300 print:bg-white"
              >
                <div className="px-4 py-3 border-b border-border text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                  {col.label}
                </div>
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="text-left text-[9px] uppercase tracking-[0.18em] text-muted-foreground border-b border-border">
                      <th className="py-1.5 px-3 font-medium">Action</th>
                      <th className="py-1.5 px-3 font-medium">Owner</th>
                      <th className="py-1.5 px-3 font-medium">Due</th>
                      <th className="py-1.5 px-3 font-medium">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {col.rows.map((r, i) => (
                      <tr
                        key={i}
                        className="border-b border-border last:border-b-0 align-top"
                      >
                        <td className="py-2 px-3">{r.action || "—"}</td>
                        <td className="py-2 px-3 text-muted-foreground">
                          {r.owner || "—"}
                        </td>
                        <td className="py-2 px-3 text-muted-foreground tabular">
                          {r.due || "—"}
                        </td>
                        <td className="py-2 px-3">
                          <span className="inline-block text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ring-1 ring-inset bg-neutral-700/40 text-neutral-300 ring-neutral-500/30 print:bg-neutral-100 print:text-neutral-700 print:ring-neutral-300">
                            Open
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </ReportSection>

        {/* Top 10 strategic targets */}
        <ReportSection number="07" title="Top 10 strategic targets">
          <div className="card-shell-2 p-5 print:border print:border-neutral-300 print:bg-white">
            <div className="text-sm text-muted-foreground italic">
              TBD — populate from account research. This section mirrors the
              territory-level target list from the reference template and is built
              from the rep&rsquo;s expanded research deck.
            </div>
          </div>
        </ReportSection>

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t border-border print:mt-10">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="inline-flex items-center bg-[#F5F1EA] rounded-md px-3 py-2 print:bg-white print:border print:border-neutral-300">
              <img
                src="brand/mi6-attilius-horizontal.png"
                alt="Mi6 Group · powered by Attilius"
                className="h-7 w-auto block"
              />
            </div>
            <div className="text-right">
              <div className="text-[13px] italic tracking-tight">
                Words talk. Numbers scream.
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mt-1 print:text-neutral-500">
                Mi6 Attilius · Sales plan report · {data.company || "—"}
              </div>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
}

/* ---------------- helpers ---------------- */

function ReportSection({
  number,
  title,
  children,
}: {
  number: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-12 print:mb-8 print:break-inside-avoid">
      <div className="flex items-baseline gap-4 mb-5">
        <span className="tabular text-[12px] text-muted-foreground tracking-[0.18em] print:text-neutral-500">
          {number}
        </span>
        <h2 className="text-2xl print:text-xl font-semibold tracking-tight">
          {title}
        </h2>
      </div>
      {children}
    </section>
  );
}

function Kpi({
  label,
  value,
  accent,
  small,
}: {
  label: string;
  value: string;
  accent?: "red" | "amber" | "green";
  small?: boolean;
}) {
  const accentClass = accent
    ? accent === "green"
      ? "text-emerald-300"
      : accent === "amber"
      ? "text-orange-300"
      : "text-rose-300"
    : "";
  return (
    <div className="card-shell-2 px-4 py-3 print:border print:border-neutral-300 print:bg-white">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground print:text-neutral-500">
        {label}
      </div>
      <div
        className={`tabular font-semibold mt-1 ${
          small ? "text-xl" : "text-3xl print:text-2xl"
        } ${accentClass}`}
      >
        {value}
      </div>
    </div>
  );
}

function RoleChip({ role }: { role: string }) {
  if (!role) return <span className="text-muted-foreground">—</span>;
  const map: Record<string, string> = {
    "Decision-maker": "bg-emerald-500/10 text-emerald-300 ring-emerald-500/30",
    Champion: "bg-emerald-500/10 text-emerald-300 ring-emerald-500/30",
    Influencer: "bg-neutral-500/10 text-neutral-300 ring-neutral-500/30",
    Coach: "bg-red-500/10 text-red-300 ring-red-500/30",
    Blocker: "bg-rose-500/10 text-rose-300 ring-rose-500/30",
    "End-user": "bg-neutral-700/40 text-neutral-300 ring-neutral-500/30",
  };
  const cls = map[role] ?? "bg-neutral-700/40 text-neutral-300 ring-neutral-500/30";
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-[10px] uppercase tracking-wider ring-1 ring-inset ${cls} print:ring-neutral-300 print:bg-white print:text-neutral-700`}
    >
      {role}
    </span>
  );
}

function StakeholderMatrix({
  stakeholders,
}: {
  stakeholders: { name: string; title: string; role: string; engagement: number }[];
}) {
  // X-axis: influence (proxy = role weight). Y-axis: engagement (1-5 inverted, top = high).
  const roleInfluence: Record<string, number> = {
    "Decision-maker": 0.9,
    Champion: 0.7,
    Influencer: 0.6,
    Coach: 0.55,
    Blocker: 0.4,
    "End-user": 0.25,
  };
  const dotColor = (role: string) => {
    if (role === "Blocker") return "bg-rose-400 ring-rose-300/40";
    if (role === "Decision-maker" || role === "Champion") return "bg-emerald-400 ring-emerald-300/40";
    if (role === "Influencer" || role === "Coach") return "bg-neutral-400 ring-neutral-300/40";
    return "bg-neutral-400 ring-neutral-300/40";
  };

  return (
    <div className="card-shell-2 p-4 print:border print:border-neutral-300 print:bg-white">
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3">
        Influence × engagement
      </div>
      <div className="relative aspect-[5/4] w-full">
        {/* Quadrant grid */}
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 border border-border print:border-neutral-300">
          <div className="border-r border-b border-border print:border-neutral-300" />
          <div className="border-b border-border print:border-neutral-300" />
          <div className="border-r border-border print:border-neutral-300" />
          <div />
        </div>
        {/* Axis labels */}
        <div className="absolute -left-1 top-1 text-[9px] uppercase tracking-wider text-muted-foreground rotate-[-90deg] origin-top-left translate-x-2 translate-y-12 print:text-neutral-500">
          Engagement →
        </div>
        <div className="absolute bottom-[-22px] left-1/2 -translate-x-1/2 text-[9px] uppercase tracking-wider text-muted-foreground print:text-neutral-500">
          Influence →
        </div>
        {/* Dots */}
        {stakeholders.map((s, i) => {
          const x = (roleInfluence[s.role] ?? 0.4) * 100;
          const y = 100 - (s.engagement / 5) * 100;
          return (
            <div
              key={i}
              className="absolute"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transform: "translate(-50%, -50%)",
              }}
              title={`${s.name} — ${s.role}`}
            >
              <span
                className={`block w-3 h-3 rounded-full ring-2 ${dotColor(s.role)}`}
              />
              <span className="absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap text-[10px] text-muted-foreground print:text-neutral-700">
                {s.name.split(" ").slice(-1)[0]}
              </span>
            </div>
          );
        })}
      </div>
      <div className="mt-8 text-[10px] text-muted-foreground italic print:text-neutral-500">
        Position by role-weighted influence and self-reported engagement.
      </div>
    </div>
  );
}

function PrintStyles() {
  return (
    <style>{`
      @media print {
        @page { size: Letter; margin: 0.6in; }
        body { background: white !important; color: black !important; }
        .print\\:hidden { display: none !important; }
        a { color: inherit; text-decoration: none; }
        .card-shell, .card-shell-2 {
          background: white !important;
          color: black !important;
        }
        .text-muted-foreground { color: #555 !important; }
        .bar-track { background: #eee !important; }
        section { break-inside: avoid; }
      }
    `}</style>
  );
}
